import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { 
  Scissors, 
  Settings, 
  Users, 
  Calendar,
  MessageSquare,
  Star,
  TrendingUp,
  Globe,
  Shield,
  Sparkles,
  Gem,
  Truck,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
  RefreshCw,
  AlertTriangle,
  Mail
} from 'lucide-react';
import AdminDisputesPanel from '../../../components/services/AdminDisputesPanel';
import AdminAppointmentNotifications from '../../../components/services/AdminAppointmentNotifications';
import AdminReviewManagement from '../../../components/services/AdminReviewManagement';
import AdminJewelleryServices from '../../../components/services/AdminJewelleryServices';
import AdminDeliveryManagement from '../../../components/services/AdminDeliveryManagement';
import AdminSportsServices from '../../../components/services/AdminSportsServices';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification level names
const VERIFICATION_LEVELS = ['New', 'Verified', 'Trusted', 'Premium', 'Elite'];

// Available categories for services with full configuration
const AVAILABLE_CATEGORIES = [
  { 
    id: 'womens-clothing', 
    name: "Women's Clothing",
    icon: '👗',
    minVerificationLevel: 1,
    bookingModes: ['fixed_price', 'quote_first', 'appointment'],
    services: ['Tailoring & Alterations', 'Custom Dress Design', 'Wedding Dress Services', 'Personal Styling', 'Wardrobe Consultation']
  },
  { 
    id: 'mens-clothing', 
    name: "Men's Clothing",
    icon: '👔',
    minVerificationLevel: 1,
    bookingModes: ['fixed_price', 'quote_first'],
    services: ['Suit Fitting & Custom Suits', 'Tailoring & Alterations', 'Personal Styling', 'Clothing Repair', 'Wardrobe Consultation']
  },
  { 
    id: 'kids-clothing', 
    name: "Kids' Clothing",
    icon: '🧒',
    minVerificationLevel: 2, // Higher trust required for kids
    bookingModes: ['fixed_price', 'appointment'],
    trustBadgeRequired: true,
    services: ['Clothing Alterations', 'School Uniform Alterations', 'Occasion Wear Fitting', 'Costume Customisation']
  },
  { 
    id: 'shoes-footwear', 
    name: 'Shoes & Footwear',
    icon: '👟',
    minVerificationLevel: 1,
    bookingModes: ['fixed_price'],
    services: ['Shoe Repair & Resole', 'Shoe Cleaning & Restoration', 'Shoe Stretching & Fitting', 'Custom Insoles', 'Sneaker Cleaning']
  },
  { 
    id: 'bags-accessories', 
    name: 'Bags & Accessories',
    icon: '👜',
    minVerificationLevel: 3, // Luxury items need higher trust
    bookingModes: ['quote_first'],
    services: ['Bag Repair', 'Bag Cleaning & Restoration', 'Strap Replacement', 'Authentication Services']
  },
  { 
    id: 'jewellery-watches', 
    name: 'Jewellery & Watches',
    icon: '💍',
    minVerificationLevel: 4, // Highest trust - certification required
    bookingModes: ['quote_first', 'appointment'],
    requiresCertification: true,
    services: ['Jewellery Repair', 'Ring Resizing', 'Watch Repair & Servicing', 'Battery Replacement', 'Jewellery Cleaning', 'Valuation & Authentication']
  },
  { 
    id: 'beauty-skincare', 
    name: 'Beauty & Skincare',
    icon: '💄',
    minVerificationLevel: 2,
    bookingModes: ['appointment', 'mobile_visit'],
    services: ['Facials & Skin Treatments', 'Makeup Services', 'Hair Styling', 'Skincare Consultation', 'Mobile Beauty Services']
  },
  { 
    id: 'perfumes-cosmetics', 
    name: 'Perfumes & Cosmetics',
    icon: '🌸',
    minVerificationLevel: 2,
    bookingModes: ['appointment'],
    services: ['Fragrance Consultation', 'Custom Perfume Blending', 'Makeup Consultation', 'Makeup Lessons']
  },
  { 
    id: 'traditional-wear', 
    name: 'Traditional Wear',
    icon: '🧵',
    minVerificationLevel: 2,
    bookingModes: ['quote_first'],
    services: ['Custom Tailoring', 'Traditional Outfit Stitching', 'Alterations & Resizing', 'Embroidery Services', 'Occasion Styling']
  },
  { 
    id: 'laundry-dryclean', 
    name: 'Laundries & Drycleaners',
    icon: '🧺',
    minVerificationLevel: 1, // Basic - admin changeable
    bookingModes: ['fixed_price', 'quote_first'],
    linkedToClothing: true, // Shows in all clothing categories
    services: ['Pick-up & Delivery', 'In-store Drop-off', 'Express Service', 'Specialty Cleaning (Leather)', 'Specialty Cleaning (Wedding Dresses)', 'Dry Cleaning', 'Ironing & Pressing']
  },
];

const ServicesAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('SL');
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Country configs
  const [countryConfigs, setCountryConfigs] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(null);
  
  // Providers
  const [providers, setProviders] = useState([]);
  
  // Bookings & Quotes
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  
  // Verification Queue
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [verificationStats, setVerificationStats] = useState(null);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verificationDecision, setVerificationDecision] = useState({
    level: 1,
    notes: '',
    achievements: []
  });

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, configsRes, configRes, providersRes, bookingsRes, quotesRes, pendingRes, verifyStatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/services/admin/stats?country=${selectedCountry}`),
        axios.get(`${API_URL}/api/services/admin/countries`),
        axios.get(`${API_URL}/api/services/admin/config?country=${selectedCountry}`),
        axios.get(`${API_URL}/api/services/admin/providers?country=${selectedCountry}&limit=20`),
        axios.get(`${API_URL}/api/services/admin/bookings?limit=20`),
        axios.get(`${API_URL}/api/services/admin/quotes?limit=20`),
        axios.get(`${API_URL}/api/services/admin/verification/pending`),
        axios.get(`${API_URL}/api/services/admin/verification/stats`),
      ]);
      
      setStats(statsRes.data);
      setCountryConfigs(configsRes.data.configs || []);
      setCurrentConfig(configRes.data);
      setProviders(providersRes.data.providers || []);
      setBookings(bookingsRes.data.bookings || []);
      setQuotes(quotesRes.data.quotes || []);
      setPendingVerifications(pendingRes.data.providers || []);
      setVerificationStats(verifyStatsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/services/admin/config`, currentConfig);
      await fetchData();
    } catch (error) {
      console.error('Failed to save config:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleProviderStatus = async (providerId) => {
    try {
      await axios.post(`${API_URL}/api/services/admin/providers/${providerId}/toggle`);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle provider:', error);
    }
  };

  const updateVerificationLevel = async (providerId, level) => {
    try {
      await axios.post(`${API_URL}/api/services/admin/providers/${providerId}/verify?level=${level}`);
      await fetchData();
    } catch (error) {
      console.error('Failed to update verification:', error);
    }
  };

  const toggleSponsor = async (providerId, isSponsored) => {
    try {
      await axios.post(`${API_URL}/api/services/admin/providers/${providerId}/sponsor?is_sponsored=${isSponsored}`);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle sponsor:', error);
    }
  };

  const toggleCategory = (categoryId) => {
    if (!currentConfig) return;
    
    const enabled = currentConfig.categories_enabled || [];
    const newEnabled = enabled.includes(categoryId)
      ? enabled.filter(c => c !== categoryId)
      : [...enabled, categoryId];
    
    setCurrentConfig({ ...currentConfig, categories_enabled: newEnabled });
  };

  const handleVerificationDecision = async (providerId, decision) => {
    try {
      await axios.post(`${API_URL}/api/services/admin/verification/${providerId}/decide`, {
        decision: decision,
        verification_level: verificationDecision.level,
        notes: verificationDecision.notes,
        achievements: verificationDecision.achievements,
      });
      
      setSelectedVerification(null);
      setVerificationDecision({ level: 1, notes: '', achievements: [] });
      await fetchData();
    } catch (error) {
      console.error('Failed to process verification:', error);
    }
  };

  const toggleAchievement = (badge) => {
    const current = verificationDecision.achievements || [];
    const newAchievements = current.includes(badge)
      ? current.filter(b => b !== badge)
      : [...current, badge];
    setVerificationDecision({ ...verificationDecision, achievements: newAchievements });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-2">Loading Service Marketplace Admin...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="services-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600">
            <Scissors className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Service Marketplace</h1>
            <p className="text-sm text-gray-500">Manage providers, bookings, and settings</p>
          </div>
        </div>
        
        {/* Country Selector */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-gray-500" />
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
                <SelectItem value="UK">🇬🇧 UK</SelectItem>
                <SelectItem value="US">🇺🇸 US</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.providers?.total || 0}</p>
                <p className="text-xs text-gray-500">Total Providers</p>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {stats?.providers?.active || 0} active · {stats?.providers?.sponsored || 0} sponsored
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.bookings?.total || 0}</p>
                <p className="text-xs text-gray-500">Total Bookings</p>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {stats?.bookings?.pending || 0} pending · {stats?.bookings?.completed || 0} completed
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <MessageSquare className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.quotes?.total || 0}</p>
                <p className="text-xs text-gray-500">Quote Requests</p>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {stats?.quotes?.pending || 0} pending responses
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.reviews?.total || 0}</p>
                <p className="text-xs text-gray-500">Total Reviews</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-10 w-full max-w-6xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="verification">Verification</TabsTrigger>
          <TabsTrigger value="disputes">Disputes</TabsTrigger>
          <TabsTrigger value="notifications">Emails</TabsTrigger>
          <TabsTrigger value="reviews">
            <Star className="h-3 w-3 mr-1" />
            Reviews
          </TabsTrigger>
          <TabsTrigger value="jewellery">
            <Gem className="h-3 w-3 mr-1" />
            Jewellery
          </TabsTrigger>
          <TabsTrigger value="delivery">
            <Truck className="h-3 w-3 mr-1" />
            Delivery
          </TabsTrigger>
          <TabsTrigger value="sports">
            🏅 Sports
          </TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Module Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Module Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Service Marketplace</p>
                    <p className="text-sm text-gray-500">Enable/disable for {selectedCountry}</p>
                  </div>
                  <Switch
                    checked={currentConfig?.enabled ?? false}
                    onCheckedChange={(checked) => 
                      setCurrentConfig({ ...currentConfig, enabled: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">AI Matched Badge</p>
                    <p className="text-sm text-gray-500">Show AI ranking indicator</p>
                  </div>
                  <Switch
                    checked={currentConfig?.show_ai_badge ?? true}
                    onCheckedChange={(checked) => 
                      setCurrentConfig({ ...currentConfig, show_ai_badge: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Sponsored Providers</p>
                    <p className="text-sm text-gray-500">Show sponsored listings</p>
                  </div>
                  <Switch
                    checked={currentConfig?.show_sponsored ?? true}
                    onCheckedChange={(checked) => 
                      setCurrentConfig({ ...currentConfig, show_sponsored: checked })
                    }
                  />
                </div>
                <Button onClick={saveConfig} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>

            {/* Verification Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Verification Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {VERIFICATION_LEVELS.map((level, idx) => {
                    const count = stats?.providers?.verification_distribution?.[`level_${idx}`] || 0;
                    const percentage = stats?.providers?.total 
                      ? Math.round((count / stats.providers.total) * 100) 
                      : 0;
                    
                    return (
                      <div key={level} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>Level {idx}: {level}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Verification Tab */}
        <TabsContent value="verification" className="space-y-4">
          {/* Verification Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-yellow-700">{verificationStats?.pending || 0}</p>
                <p className="text-sm text-yellow-600">Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{verificationStats?.under_review || 0}</p>
                <p className="text-sm text-blue-600">Under Review</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-700">{verificationStats?.approved || 0}</p>
                <p className="text-sm text-green-600">Approved</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-700">{verificationStats?.rejected || 0}</p>
                <p className="text-sm text-red-600">Rejected</p>
              </CardContent>
            </Card>
          </div>

          {/* Pending Verifications List */}
          <Card>
            <CardHeader>
              <CardTitle>Verification Queue</CardTitle>
              <CardDescription>Review and approve new service providers</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingVerifications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p>All verifications processed!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingVerifications.map((provider) => (
                    <div key={provider.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{provider.business_name}</h3>
                          <p className="text-sm text-gray-500">{provider.provider_name} • {provider.service_type}</p>
                          <p className="text-sm text-gray-500 mt-1">{provider.city}, {provider.country}</p>
                          <p className="text-sm text-gray-600 mt-2">{provider.description}</p>
                          
                          {/* Specializations */}
                          {provider.specializations?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {provider.specializations.map((spec, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {spec}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          {/* Documents */}
                          {provider.verification_documents?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-gray-500 mb-1">Documents:</p>
                              <div className="flex gap-2">
                                {provider.verification_documents.map((doc, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {doc.type}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* User Info */}
                          {provider.user_info && (
                            <p className="text-xs text-gray-400 mt-2">
                              Email: {provider.user_info.email} • Phone: {provider.user_info.phone}
                            </p>
                          )}
                        </div>
                        
                        <Badge className={
                          provider.verification_status === 'pending' 
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-blue-100 text-blue-700'
                        }>
                          {provider.verification_status}
                        </Badge>
                      </div>
                      
                      {/* Action Panel */}
                      {selectedVerification === provider.id ? (
                        <div className="mt-4 pt-4 border-t space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Verification Level</Label>
                              <Select 
                                value={verificationDecision.level.toString()}
                                onValueChange={(val) => setVerificationDecision({
                                  ...verificationDecision, 
                                  level: parseInt(val)
                                })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {VERIFICATION_LEVELS.map((level, idx) => (
                                    <SelectItem key={idx} value={idx.toString()}>
                                      Level {idx}: {level}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Notes</Label>
                              <Input
                                value={verificationDecision.notes}
                                onChange={(e) => setVerificationDecision({
                                  ...verificationDecision,
                                  notes: e.target.value
                                })}
                                placeholder="Add notes..."
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label>Award Badges</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {['verified_tailor', 'fast_response', 'top_rated', 'master_craftsman'].map((badge) => (
                                <Badge
                                  key={badge}
                                  variant={verificationDecision.achievements?.includes(badge) ? 'default' : 'outline'}
                                  className="cursor-pointer"
                                  onClick={() => toggleAchievement(badge)}
                                >
                                  {badge.replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleVerificationDecision(provider.id, 'approved')}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={() => handleVerificationDecision(provider.id, 'rejected')}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => setSelectedVerification(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 pt-4 border-t">
                          <Button 
                            onClick={() => setSelectedVerification(provider.id)}
                          >
                            Review Application
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Disputes Tab */}
        <TabsContent value="disputes">
          <AdminDisputesPanel />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <AdminAppointmentNotifications country={selectedCountry} />
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews">
          <AdminReviewManagement country={selectedCountry} />
        </TabsContent>

        {/* Jewellery Services Tab */}
        <TabsContent value="jewellery">
          <AdminJewelleryServices />
        </TabsContent>

        {/* Delivery Management Tab */}
        <TabsContent value="delivery">
          <AdminDeliveryManagement />
        </TabsContent>

        {/* Sports Tab */}
        <TabsContent value="sports">
          <AdminSportsServices />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Country Settings: {selectedCountry}</CardTitle>
              <CardDescription>Configure service marketplace for this country</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Categories */}
              <div>
                <Label className="text-base font-medium">Enabled Categories</Label>
                <p className="text-sm text-gray-500 mb-3">Select which categories show services. Each has specific verification requirements.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AVAILABLE_CATEGORIES.map((cat) => {
                    const isEnabled = currentConfig?.categories_enabled?.includes(cat.id);
                    return (
                      <div
                        key={cat.id}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isEnabled ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => toggleCategory(cat.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isEnabled ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                          }`}>
                            {isEnabled && <CheckCircle2 className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{cat.icon}</span>
                              <span className="font-medium text-sm">{cat.name}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <Badge variant="outline" className="text-xs">
                                Min L{cat.minVerificationLevel}
                              </Badge>
                              {cat.requiresCertification && (
                                <Badge className="text-xs bg-amber-100 text-amber-700">
                                  Cert Required
                                </Badge>
                              )}
                              {cat.trustBadgeRequired && (
                                <Badge className="text-xs bg-blue-100 text-blue-700">
                                  Trust Badge
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {cat.bookingModes.join(', ').replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Display Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Providers Shown</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={currentConfig?.max_providers_shown || 4}
                    onChange={(e) => setCurrentConfig({ 
                      ...currentConfig, 
                      max_providers_shown: parseInt(e.target.value) || 4 
                    })}
                  />
                </div>
                <div>
                  <Label>Sponsor Slots</Label>
                  <Input
                    type="number"
                    min={0}
                    max={5}
                    value={currentConfig?.sponsor_slots || 2}
                    onChange={(e) => setCurrentConfig({ 
                      ...currentConfig, 
                      sponsor_slots: parseInt(e.target.value) || 2 
                    })}
                  />
                </div>
              </div>

              {/* Monetization */}
              <div>
                <Label className="text-base font-medium">Monetization</Label>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <Label className="text-xs">Booking Commission %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={50}
                      value={currentConfig?.monetization?.booking_commission_percent || 10}
                      onChange={(e) => setCurrentConfig({
                        ...currentConfig,
                        monetization: {
                          ...currentConfig?.monetization,
                          booking_commission_percent: parseInt(e.target.value) || 10
                        }
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Quote Lead Fee ({currentConfig?.currency})</Label>
                    <Input
                      type="number"
                      min={0}
                      value={currentConfig?.monetization?.quote_lead_fee || 50}
                      onChange={(e) => setCurrentConfig({
                        ...currentConfig,
                        monetization: {
                          ...currentConfig?.monetization,
                          quote_lead_fee: parseInt(e.target.value) || 50
                        }
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Sponsor Daily Rate ({currentConfig?.currency})</Label>
                    <Input
                      type="number"
                      min={0}
                      value={currentConfig?.monetization?.sponsor_daily_rate || 500}
                      onChange={(e) => setCurrentConfig({
                        ...currentConfig,
                        monetization: {
                          ...currentConfig?.monetization,
                          sponsor_daily_rate: parseInt(e.target.value) || 500
                        }
                      })}
                    />
                  </div>
                </div>
              </div>

              <Button onClick={saveConfig} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Settings
              </Button>
            </CardContent>
          </Card>

          {/* Country Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Countries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {countryConfigs.map((config) => (
                  <div 
                    key={config.country}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">
                        {config.country === 'SL' ? '🇱🇰' : config.country === 'UK' ? '🇬🇧' : '🇺🇸'}
                      </span>
                      <div>
                        <p className="font-medium">{config.country}</p>
                        <p className="text-xs text-gray-500">
                          {config.categories_enabled?.length || 0} categories
                        </p>
                      </div>
                    </div>
                    <Badge variant={config.enabled ? 'default' : 'secondary'}>
                      {config.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Service Providers</CardTitle>
              <CardDescription>Manage provider verification and status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {providers.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No providers found</p>
                ) : (
                  providers.map((provider) => (
                    <div 
                      key={provider.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Scissors className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{provider.provider_name}</p>
                          <p className="text-sm text-gray-500">{provider.service_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              Level {provider.verification_level}: {VERIFICATION_LEVELS[provider.verification_level]}
                            </Badge>
                            {provider.is_sponsored && (
                              <Badge className="text-xs bg-purple-100 text-purple-700">
                                <Sparkles className="h-3 w-3 mr-1" />
                                Sponsored
                              </Badge>
                            )}
                            <span className="flex items-center gap-0.5 text-xs">
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                              {provider.rating?.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Verification Level */}
                        <Select 
                          value={provider.verification_level?.toString()}
                          onValueChange={(val) => updateVerificationLevel(provider.id, parseInt(val))}
                        >
                          <SelectTrigger className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VERIFICATION_LEVELS.map((level, idx) => (
                              <SelectItem key={idx} value={idx.toString()}>
                                Level {idx}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Sponsor Toggle */}
                        <Button
                          size="sm"
                          variant={provider.is_sponsored ? 'default' : 'outline'}
                          className="h-8"
                          onClick={() => toggleSponsor(provider.id, !provider.is_sponsored)}
                        >
                          <Sparkles className="h-3 w-3" />
                        </Button>
                        
                        {/* Active Toggle */}
                        <Button
                          size="sm"
                          variant={provider.is_active ? 'default' : 'destructive'}
                          className="h-8"
                          onClick={() => toggleProviderStatus(provider.id)}
                        >
                          {provider.is_active ? (
                            <CheckCircle2 className="h-4 w-4" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recent Bookings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Recent Bookings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {bookings.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No bookings yet</p>
                  ) : (
                    bookings.slice(0, 5).map((booking) => (
                      <div key={booking.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{booking.service_name}</p>
                            <p className="text-xs text-gray-500">{booking.customer_name}</p>
                          </div>
                          <Badge variant={
                            booking.status === 'completed' ? 'default' :
                            booking.status === 'pending' ? 'secondary' : 'outline'
                          }>
                            {booking.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(booking.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Quotes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Recent Quote Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {quotes.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No quote requests yet</p>
                  ) : (
                    quotes.slice(0, 5).map((quote) => (
                      <div key={quote.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{quote.work_type}</p>
                            <p className="text-xs text-gray-500">{quote.customer_name}</p>
                          </div>
                          <Badge variant={
                            quote.status === 'quoted' ? 'default' :
                            quote.status === 'pending' ? 'secondary' : 'outline'
                          }>
                            {quote.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Budget: {quote.budget_range || 'Not specified'}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ServicesAdminDashboard;
